import { useState, useMemo, useEffect } from 'react';

export const PAGE_SIZE = 200;

function RankingsTable({ rankings, aggregated, posFilter = 'ALL', onPageInfoChange }) {
  const [sortField, setSortField] = useState(aggregated ? 'avgRank' : 'rank');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);

  const sourceNames = useMemo(() => {
    if (!aggregated || rankings.length === 0) return [];
    const names = new Set();
    rankings.forEach(r => {
      if (r.ranksBySource) {
        Object.keys(r.ranksBySource).forEach(s => names.add(s));
      }
    });
    return [...names].sort();
  }, [rankings, aggregated]);

  const sorted = useMemo(() => {
    const copy = [...rankings];
    copy.sort((a, b) => {
      let aVal, bVal;
      if (sortField.startsWith('source:')) {
        const src = sortField.slice(7);
        aVal = a.posRanksBySource?.[src] ?? Infinity;
        bVal = b.posRanksBySource?.[src] ?? Infinity;
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortAsc ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [rankings, sortField, sortAsc]);

  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [rankings, sortField, sortAsc]);

  useEffect(() => {
    onPageInfoChange?.({ page, pageCount });
  }, [page, pageCount, onPageInfoChange]);

  function handleSort(field) {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  }

  function sortIndicator(field) {
    if (sortField !== field) return '';
    return sortAsc ? ' \u25B2' : ' \u25BC';
  }

  function stdDevClass(val) {
    if (val <= 1) return 'stat-low';
    if (val <= 3) return 'stat-mid';
    return 'stat-high';
  }

  const paginationControls = pageCount > 1 && (
    <div className='pagination-controls'>
      <button
        className='btn btn-secondary'
        onClick={() => setPage(p => p - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className='pagination-label'>Page {page} of {pageCount}</span>
      <button
        className='btn btn-secondary'
        onClick={() => setPage(p => p + 1)}
        disabled={page === pageCount}
      >
        Next
      </button>
    </div>
  );

  if (rankings.length === 0) {
    return (
      <div className='empty-state'>
        <h3>No rankings found</h3>
        <p>Upload rankings or wait for the next scraper run.</p>
      </div>
    );
  }

  if (aggregated) {
    return (
      <>
      <div className='table-container'>
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('avgRank')}>#Ovr{sortIndicator('avgRank')}</th>
              <th onClick={() => handleSort('avgPositionRank')}>#Pos{sortIndicator('avgPositionRank')}</th>
              <th onClick={() => handleSort('playerName')}>Player{sortIndicator('playerName')}</th>
              <th onClick={() => handleSort('position')}>Pos{sortIndicator('position')}</th>
              <th onClick={() => handleSort('team')}>Team{sortIndicator('team')}</th>
              {sourceNames.map(src => (
                <th key={src} onClick={() => handleSort(`source:${src}`)}>{src}{sortIndicator(`source:${src}`)}</th>
              ))}
              <th onClick={() => handleSort('highRank')}>Best{sortIndicator('highRank')}</th>
              <th onClick={() => handleSort('lowRank')}>Worst{sortIndicator('lowRank')}</th>
              <th onClick={() => handleSort('stdDev')}>StdDev{sortIndicator('stdDev')}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.playerName}>
                <td>{r.avgRank?.toFixed(1) ?? '-'}</td>
                <td>{r.avgPositionRank?.toFixed(1) ?? '-'}</td>
                <td><strong>{r.playerName}</strong></td>
                <td><span className={`pos-badge pos-${r.position}`}>{r.position}</span></td>
                <td>{r.team}</td>
                {sourceNames.map(src => {
                  const posRank = r.posRanksBySource?.[src];
                  const ovrRank = r.ranksBySource?.[src];
                  if (posFilter === 'ALL') {
                    return <td key={src}>{ovrRank ?? '-'}</td>;
                  }
                  if (posRank == null) return <td key={src}>-</td>;
                  return (
                    <td key={src}>
                      {posRank}
                      {ovrRank != null && <span className='ovr-rank'> (#{ovrRank})</span>}
                    </td>
                  );
                })}
                <td>{(posFilter === 'ALL' ? r.highOvrRank : r.highRank) || '-'}</td>
                <td>{(posFilter === 'ALL' ? r.lowOvrRank : r.lowRank) || '-'}</td>
                <td><span className={`stat-badge ${stdDevClass(posFilter === 'ALL' ? r.stdDevOvr : r.stdDev)}`}>{(posFilter === 'ALL' ? r.stdDevOvr : r.stdDev)?.toFixed(1) ?? '-'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginationControls}
      </>
    );
  }

  return (
    <>
    <div className='table-container'>
      <table>
        <thead>
          <tr>
            <th onClick={() => handleSort('rank')}>#Ovr{sortIndicator('rank')}</th>
            <th onClick={() => handleSort('positionRank')}>#Pos{sortIndicator('positionRank')}</th>
            <th onClick={() => handleSort('playerName')}>Player{sortIndicator('playerName')}</th>
            <th onClick={() => handleSort('position')}>Pos{sortIndicator('position')}</th>
            <th onClick={() => handleSort('team')}>Team{sortIndicator('team')}</th>
            <th onClick={() => handleSort('source')}>Source{sortIndicator('source')}</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((r) => (
            <tr key={`${r.source}-${r.playerName}`}>
              <td>{r.rank}</td>
              <td>{r.positionRank || '-'}</td>
              <td><strong>{r.playerName}</strong></td>
              <td><span className={`pos-badge pos-${r.position}`}>{r.position}</span></td>
              <td>{r.team}</td>
              <td>{r.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {paginationControls}
    </>
  );
}

export default RankingsTable;
